import { DatePipe, TitleCasePipe } from "@angular/common";
import { Component, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { IApiResponse } from "@models/api-response.model";
import { RequestService } from "@services/index";

interface OutreachChannel {
  channel_key: string;
  label: string;
  chat_id: string;
  iso2: string;
  is_active: boolean;
  frequency: "daily" | "weekly" | "monthly";
  send_days: number[];
  send_hour: number;
  send_minute: number;
  product_count: number;
  category_ids: number[];
  last_sent_at: string | null;
  /** Whether a token is stored. The value is never sent to the client. */
  has_bot_token?: boolean;
  /** Last four characters, so an admin can tell which token is in place. */
  bot_token_hint?: string | null;
}

const WEEKDAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 7, label: "Sun" },
];

/** Freshwater and Marine Livestock, the only categories worth posting to a fish channel. */
const CATEGORIES = [
  { id: 1, label: "Freshwater Livestock" },
  { id: 240, label: "Marine Livestock" },
];

/**
 * Control panel for the SG Telegram channel.
 *
 * Posts the most recent products a shopper can actually buy - one message each,
 * with an image, the price, a couple of lines of description and a link back to
 * the product. Nothing is sent unless the channel is switched on.
 *
 * "Preview" and "Send now" exist because the alternative is discovering what a
 * run looks like when it lands in front of subscribers. Preview lists what would
 * go out; Send now posts for real, and works while inactive so a first test post
 * does not require arming the schedule.
 */
@Component({
  selector: "fh-sg-telegram-panel",
  templateUrl: "./sg-telegram-panel.component.html",
  styleUrls: ["./sg-telegram-panel.component.scss"],
  standalone: true,
  imports: [DatePipe, TitleCasePipe, FormsModule, MatButtonModule, MatIconModule, MatTooltipModule],
})
export class SgTelegramPanelComponent implements OnInit {
  readonly weekdays = WEEKDAYS;
  readonly categories = CATEGORIES;
  /** 1-28: a 29th-31st schedule would skip February, which reads as a broken job. */
  readonly monthDays = Array.from({ length: 28 }, (_, i) => i + 1);

  channel = signal<OutreachChannel | null>(null);
  loading = signal<boolean>(true);
  saving = signal<boolean>(false);
  busy = signal<boolean>(false);
  message = signal<string>("");
  error = signal<string>("");
  preview = signal<{ id: number; name: string; price: string | null }[] | null>(null);
  /** Never populated from the server - the token is write-only. */
  tokenInput = "";
  savingToken = signal<boolean>(false);

  constructor(private requestService: RequestService) {}

  ngOnInit() {
    this.requestService.get<IApiResponse>("/api/v2/outreach/channels").subscribe({
      next: (res) => {
        this.loading.set(false);
        const list = (res.data as { channels: OutreachChannel[] } | undefined)?.channels ?? [];
        const sg = list.find((c) => c.channel_key === "sg-telegram") ?? null;
        if (!sg) {
          this.error.set("The SG Telegram channel is not configured on this backend.");
          return;
        }
        this.channel.set(sg);
      },
      error: () => {
        this.loading.set(false);
        this.error.set("Could not load the channel settings");
      },
    });
  }

  /** Signals hold a whole object, so every edit replaces it rather than mutating. */
  private patch(changes: Partial<OutreachChannel>) {
    const c = this.channel();
    if (c) this.channel.set({ ...c, ...changes });
  }

  setActive(value: boolean) {
    this.patch({ is_active: value });
  }

  setFrequency(value: OutreachChannel["frequency"]) {
    // Days carry no meaning across a change of frequency - day 5 of the week is
    // not day 5 of the month - so the selection starts clean.
    this.patch({ frequency: value, send_days: [] });
  }

  toggleDay(day: number) {
    const c = this.channel();
    if (!c) return;
    const days = c.send_days.includes(day)
      ? c.send_days.filter((d) => d !== day)
      : [...c.send_days, day].sort((a, b) => a - b);
    this.patch({ send_days: days });
  }

  isDaySelected(day: number): boolean {
    return this.channel()?.send_days.includes(day) ?? false;
  }

  toggleCategory(id: number) {
    const c = this.channel();
    if (!c) return;
    const ids = c.category_ids.includes(id)
      ? c.category_ids.filter((x) => x !== id)
      : [...c.category_ids, id].sort((a, b) => a - b);
    this.patch({ category_ids: ids });
  }

  isCategorySelected(id: number): boolean {
    return this.channel()?.category_ids.includes(id) ?? false;
  }

  setHour(value: string) {
    this.patch({ send_hour: Math.min(23, Math.max(0, Number(value) || 0)) });
  }

  setMinute(value: string) {
    this.patch({ send_minute: Math.min(59, Math.max(0, Number(value) || 0)) });
  }

  setCount(value: string) {
    this.patch({ product_count: Math.min(50, Math.max(1, Number(value) || 1)) });
  }

  /** Mirrors the server's own rules, so the button explains itself before a round trip. */
  validationError(): string {
    const c = this.channel();
    if (!c) return "";
    if (c.frequency === "weekly" && !c.send_days.length) return "Pick at least one day of the week";
    if (c.frequency === "monthly" && !c.send_days.length)
      return "Pick at least one day of the month";
    if (!c.category_ids.length) return "Pick at least one category";
    return "";
  }

  save() {
    const c = this.channel();
    if (!c || this.validationError()) return;
    this.saving.set(true);
    this.message.set("");
    this.error.set("");
    this.requestService
      .put<IApiResponse>(`/api/v2/outreach/channels/${c.channel_key}`, {
        is_active: c.is_active,
        frequency: c.frequency,
        send_days: c.send_days,
        send_hour: c.send_hour,
        send_minute: c.send_minute,
        product_count: c.product_count,
        category_ids: c.category_ids,
      })
      .subscribe({
        next: (res) => {
          this.saving.set(false);
          if (!res.status) {
            this.error.set(res.message || "Could not save");
            return;
          }
          const saved = (res.data as { channel: OutreachChannel } | undefined)?.channel;
          if (saved) this.channel.set(saved);
          this.message.set("Saved.");
        },
        error: () => {
          this.saving.set(false);
          this.error.set("Could not save");
        },
      });
  }

  loadPreview() {
    const c = this.channel();
    if (!c) return;
    this.busy.set(true);
    this.message.set("");
    this.error.set("");
    this.requestService
      .get<IApiResponse>(`/api/v2/outreach/channels/${c.channel_key}/preview`)
      .subscribe({
        next: (res) => {
          this.busy.set(false);
          this.preview.set((res.data as { products: never[] } | undefined)?.products ?? []);
        },
        error: () => {
          this.busy.set(false);
          this.error.set("Could not load the preview");
        },
      });
  }

  sendNow() {
    const c = this.channel();
    if (!c) return;
    if (
      !confirm(
        `Post ${c.product_count} product(s) to ${c.label} now? This is a real post to the channel.`
      )
    ) {
      return;
    }
    this.busy.set(true);
    this.message.set("");
    this.error.set("");
    this.requestService
      .post<IApiResponse>(`/api/v2/outreach/channels/${c.channel_key}/run`, {})
      .subscribe({
        next: (res) => {
          this.busy.set(false);
          const r = (res.data as { result?: { sent: number; failed: number } } | undefined)?.result;
          this.message.set(
            r ? `Posted ${r.sent}${r.failed ? `, ${r.failed} failed` : ""}.` : "Done."
          );
          this.preview.set(null);
        },
        error: () => {
          this.busy.set(false);
          this.error.set("Could not send");
        },
      });
  }

  /**
   * Save or clear the bot token.
   *
   * The server verifies it can actually post to this channel before storing it,
   * so a wrong paste fails here with a reason rather than as a silent no-post on
   * the next scheduled run.
   */
  saveToken(clear = false) {
    const c = this.channel();
    if (!c) return;
    const value = clear ? "" : this.tokenInput.trim();
    if (!clear && !value) return;
    if (clear && !confirm("Clear the stored token? Sending falls back to the server's own.")) {
      return;
    }

    this.savingToken.set(true);
    this.message.set("");
    this.error.set("");
    this.requestService
      .put<IApiResponse>(`/api/v2/outreach/channels/${c.channel_key}/token`, {
        bot_token: value,
      })
      .subscribe({
        next: (res) => {
          this.savingToken.set(false);
          if (!res.status) {
            this.error.set(res.message || "Could not save the token");
            return;
          }
          // Cleared immediately: it is never read back, so holding it serves
          // nothing and leaves a credential sitting in the DOM.
          this.tokenInput = "";
          const saved = (res.data as { channel: OutreachChannel } | undefined)?.channel;
          if (saved) this.channel.set(saved);
          this.message.set(res.message || "Token saved.");
        },
        error: () => {
          this.savingToken.set(false);
          this.error.set("Could not save the token");
        },
      });
  }
}
